import React from 'react';
import styles from '../css/vdr.module.css';
import { StatusDot, normalizeStatus } from '../components/StatusUI';
import { VDRDocumentIcon, fileTypeLabel } from '../components/VDRItemIcons';
import { activeIndexingFileIds, failedIndexingFileIds } from '../../../utils/vdrIndexingQueue';

const fileExtension = name => {
    const parts = String(name || '').split('?')[0].split('.');
    return parts.length > 1 ? parts.pop() : '';
};

const resultName = (file, document) =>
    document?.document_name ||
    file.document_name ||
    file.metadata_storage_name ||
    file.name ||
    file.title ||
    file.file_name ||
    'Untitled';

const formatSnippet = value => {
    let text = String(value || '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^[“"'`]+|[”"'`]+$/g, '')
        .replace(/^[.…\s]+|[.…\s]+$/g, '');
    if (!text) return '';
    if (text.length > 88) {
        text = text
            .slice(0, 88)
            .replace(/\s+\S*$/, '')
            .replace(/[.,;:]+$/, '');
    }
    return `…${text}…`;
};

const workstreamIdOf = (file, document) => {
    if (file.kind === 'mail') return 'mail';
    if (file.kind === 'recording') return 'recording';
    const id = file.workstream_id || document?.workstream_id;
    return id != null && id !== '' ? String(id) : '';
};

const workstreamNameOf = (file, document, workstreams) => {
    if (file.kind === 'mail') return 'Project Mail';
    if (file.kind === 'recording') return 'Meeting Recordings';
    if (file.workstream_name) return file.workstream_name;
    const fromDoc = document?.workstream;
    if (fromDoc && fromDoc !== 'No Workstream') return fromDoc;
    const match = (workstreams || []).find(item => String(item.id) === workstreamIdOf(file, document));
    return match?.name || '';
};

const pathOf = (file, document, workstreamName) => {
    if (file.kind === 'mail' || file.kind === 'recording') return file.path || workstreamName;
    const folderName = file.ws_folder || document?.ws_folder || file.folder_name || file.ws_folder_name || '';
    const parts = [workstreamName, folderName].filter(Boolean);
    if (parts.length) return parts.join(' → ');
    if (file.path && file.path !== 'VDR') return file.path;
    if (file.folder_path) return file.folder_path;
    return '';
};

const STATUS_OPTIONS = [
    { value: 'all', label: 'All statuses' },
    { value: 'ready', label: 'Ready' },
    { value: 'processing', label: 'Processing' },
    { value: 'needs_attention', label: 'Needs attention' },
];

const FilterChip = ({ id, label, value, options, open, onToggle, onChange }) => {
    const selected = options.find(option => option.value === value);
    const text = !value || value === 'all' ? label : selected?.label || label;
    return (
        <div className={styles.searchFilterWrap}>
            <button
                type="button"
                className={`${styles.searchFilterChip} ${open ? styles.searchFilterChipOpen : ''} ${
                    value && value !== 'all' ? styles.searchFilterChipActive : ''
                }`}
                aria-expanded={open}
                aria-haspopup="menu"
                onClick={() => onToggle(id)}
            >
                <span className={styles.searchFilterLabel}>{text}</span>
                <span aria-hidden>▾</span>
            </button>
            {open && (
                <div className={styles.searchFilterMenu} role="menu">
                    {options.map(option => (
                        <button
                            key={option.value}
                            type="button"
                            role="menuitemradio"
                            aria-checked={option.value === value}
                            className={`${styles.searchFilterOption} ${option.value === value ? styles.searchFilterOptionActive : ''}`}
                            onClick={() => onChange(option.value)}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const SearchView = ({
    query,
    setQuery,
    results = [],
    loading,
    onSearch,
    onOpenResult,
    documents = [],
    workstreams = [],
    indexingFiles = [],
    projectId,
}) => {
    const [openFilter, setOpenFilter] = React.useState(null);
    const [workstreamFilter, setWorkstreamFilter] = React.useState('all');
    const [typeFilter, setTypeFilter] = React.useState('all');
    const [statusFilter, setStatusFilter] = React.useState('all');
    const filtersRef = React.useRef(null);

    React.useEffect(() => {
        if (!openFilter) return undefined;
        const onDoc = event => {
            if (filtersRef.current && !filtersRef.current.contains(event.target)) setOpenFilter(null);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [openFilter]);

    const indexingIds = React.useMemo(
        () => new Set(activeIndexingFileIds(indexingFiles, { projectId })),
        [indexingFiles, projectId],
    );
    const failedIds = React.useMemo(
        () => new Set(failedIndexingFileIds(indexingFiles, { projectId })),
        [indexingFiles, projectId],
    );
    const documentsById = React.useMemo(() => {
        const map = new Map();
        (documents || []).forEach(document => {
            if (document?.id != null) map.set(String(document.id), document);
        });
        return map;
    }, [documents]);

    const rows = React.useMemo(
        () =>
            (results || []).map(file => {
                const document = documentsById.get(String(file.file_id || file.id));
                const name = resultName(file, document);
                const extension = fileExtension(document?.document_name || name);
                const id = Number(file.file_id || file.id);
                let status = normalizeStatus(file.indexing_status || file.status || file.index_status);
                if (failedIds.has(id) || failedIds.has(file.file_id) || failedIds.has(file.id)) status = 'needs_attention';
                else if (indexingIds.has(id) || indexingIds.has(file.file_id) || indexingIds.has(file.id)) status = 'processing';
                const workstreamName = workstreamNameOf(file, document, workstreams);
                return {
                    ...file,
                    document: document?.document || file.document,
                    document_link: document?.document_link || file.document_link,
                    displayName: name,
                    extension,
                    typeLabel: fileTypeLabel(extension),
                    displayPath: pathOf(file, document, workstreamName),
                    displaySnippet: formatSnippet(file.snippet || file.highlight || ''),
                    displayStatus: status,
                    workstreamName,
                    workstreamId: workstreamIdOf(file, document),
                };
            }),
        [results, documentsById, failedIds, indexingIds, workstreams],
    );

    const workstreamOptions = React.useMemo(() => {
        const options = [{ value: 'all', label: 'All workstreams' }];
        (workstreams || [])
            .filter(item => item?.id != null && item?.name)
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name))
            .forEach(item => options.push({ value: String(item.id), label: item.name }));
        if (rows.some(row => row.kind === 'mail')) options.push({ value: 'mail', label: 'Project Mail' });
        if (rows.some(row => row.kind === 'recording')) options.push({ value: 'recording', label: 'Meeting Recordings' });
        return options;
    }, [workstreams, rows]);

    const typeOptions = React.useMemo(() => {
        const labels = new Set(rows.map(row => row.typeLabel).filter(Boolean));
        return [{ value: 'all', label: 'All file types' }, ...[...labels].sort((a, b) => a.localeCompare(b)).map(label => ({ value: label, label }))];
    }, [rows]);

    const filtered = rows.filter(row => {
        if (workstreamFilter !== 'all' && row.workstreamId !== workstreamFilter) return false;
        if (typeFilter !== 'all' && row.typeLabel !== typeFilter) return false;
        if (statusFilter !== 'all' && row.displayStatus !== statusFilter) return false;
        return true;
    });

    const countLabel = loading
        ? 'Searching…'
        : `${filtered.length} result${filtered.length === 1 ? '' : 's'}`;

    const closeAndSet = setter => value => {
        setter(value);
        setOpenFilter(null);
    };

    return (
        <div className={styles.searchView}>
            <label className={styles.vdrSearchField}>
                <span className={styles.vdrSearchIcon} aria-hidden>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="6.2" cy="6.2" r="4.4" stroke="currentColor" strokeWidth="1.3" />
                        <path d="M9.4 9.4L12.2 12.2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                    </svg>
                </span>
                <input
                    value={query || ''}
                    onChange={e => setQuery?.(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === 'Enter') onSearch?.(query);
                    }}
                    placeholder="Search VDR…"
                    aria-label="Search VDR"
                />
            </label>

            <div className={styles.searchFilters} ref={filtersRef}>
                <FilterChip
                    id="workstream"
                    label="Workstream"
                    value={workstreamFilter}
                    options={workstreamOptions}
                    open={openFilter === 'workstream'}
                    onToggle={id => setOpenFilter(current => (current === id ? null : id))}
                    onChange={closeAndSet(setWorkstreamFilter)}
                />
                <FilterChip
                    id="type"
                    label="File type"
                    value={typeFilter}
                    options={typeOptions}
                    open={openFilter === 'type'}
                    onToggle={id => setOpenFilter(current => (current === id ? null : id))}
                    onChange={closeAndSet(setTypeFilter)}
                />
                <FilterChip
                    id="status"
                    label="Status"
                    value={statusFilter}
                    options={STATUS_OPTIONS}
                    open={openFilter === 'status'}
                    onToggle={id => setOpenFilter(current => (current === id ? null : id))}
                    onChange={closeAndSet(setStatusFilter)}
                />
                <span className={styles.resultCount}>{countLabel}</span>
            </div>

            {filtered.length > 0 && (
                <div className={styles.searchResults} role="list">
                    {filtered.map((file, idx) => (
                        <div
                            key={file.id || file.file_id || idx}
                            className={styles.searchResultRow}
                            onClick={() => onOpenResult?.(file)}
                            role="listitem"
                            tabIndex={0}
                            onKeyDown={e => {
                                if (e.key === 'Enter') onOpenResult?.(file);
                            }}
                        >
                            <div className={styles.searchResultMain}>
                                <VDRDocumentIcon file={file} extension={file.extension} className={styles.searchFileIcon} />
                                <div className={styles.searchResultCopy}>
                                    <div className={styles.searchResultName}>{file.displayName}</div>
                                    {file.displayPath ? <div className={styles.searchResultPath}>{file.displayPath}</div> : null}
                                    {file.displaySnippet ? (
                                        <div className={styles.searchResultSnippet}>{`“${file.displaySnippet}”`}</div>
                                    ) : null}
                                </div>
                            </div>
                            <span className={styles.searchResultStatus}>
                                <StatusDot status={file.displayStatus} />
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {!loading && query && filtered.length === 0 && <div className={styles.searchEmpty}>No results</div>}
        </div>
    );
};

export default SearchView;
